import { Injectable } from '@odinjs/odin';

export interface DownloadOptions {
  mimeType?: string;
  byteOrderMark?: string | Uint8Array;
  encoding?: string;
}

@Injectable ({})
export class FileService {

  private static clean(link: HTMLAnchorElement): void {
    // allow time for iOS
    setTimeout(() => {
      window.URL.revokeObjectURL(link.href);
    }, 10000);

    link.remove();
  }

  static exportFile(
    fileName: string,
    rawData: string|Blob,
    opts: string | DownloadOptions = {}) : boolean | Error {
    const { mimeType, byteOrderMark, encoding } = typeof opts === 'string'
      ? { mimeType: opts, byteOrderMark: undefined, encoding: undefined }
      : opts;

    const data = encoding !== void 0
      ? (new TextEncoder()).encode(rawData as string)
      : rawData;

    const blobData = byteOrderMark !== void 0 ? [byteOrderMark, data] : [data];
    const blob = new Blob(blobData, { type: mimeType || 'application/octet-stream' });
    const link = document.createElement('a');

    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', fileName);

    // Check for "download" attribute support;
    // If not supported, open this in new window
    if (typeof link.download === 'undefined') {
      link.setAttribute('target', '_blank');
    }

    link.classList.add('hidden');
    link.style.position = 'fixed'; // avoid scrolling to bottom
    document.body.appendChild(link);

    try {
      link.click();
      this.clean(link);
      return true;
    }
    catch (err) {
      this.clean(link);
      return (err as Error);
    }
  }

  static formatContentPDF(base64: string): Blob {
    return this.base64ToBlob(this.formatToBase64(base64, 'application/pdf'));
  }

  static formatToBase64(content: string, contentType: string | null ='application/octet-stream'): string {
    if (!contentType) {
      contentType = 'application/octet-stream';
    }
    return `data:${contentType};base64,${content}`;
  }

  static blobToBase64(input: Blob|undefined): Promise<string|ArrayBuffer|null|undefined> {
    return new Promise((resolve) => {
      if (!input) {
        resolve(undefined);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(input);
    });
  }

  static base64ToBlob(input: string): Blob {
    const content = input.split(',')[1];
    const start = input.indexOf(':') + 1;
    const end = input.indexOf(';') - start;
    const contentType = input.substr(start, end);
    const byteCharacters = atob(content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

}
